import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store/store';
import { WebBuilderPreloader } from '@/components/webbuilder/ui/loaders/WebBuilderPreloader';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { isStoreKycApprovedForPublishing } from '@/utils/storeKyc';

export function StoreGuard({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { storeType, storeId } = useParams<{ storeType: string; storeId: string }>();
  const { statusLoading, isAuthenticated, verifiedStores } = useAuthStatus();
  const { currentStoreInfo } = useSelector((state: RootState) => state.auth);
  const [validating, setValidating] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [showKycRedirectModal, setShowKycRedirectModal] = useState(false);
  const [pendingKycRedirectPath, setPendingKycRedirectPath] = useState<string | null>(null);

  useEffect(() => {
    async function checkAccess() {
      if (statusLoading) return;

      if (!isAuthenticated) {
        navigate('/auth/login', { replace: true });
        return;
      }

      if (!storeType || !storeId) {
        navigate('/auth/store-selector?error=invalid-store', { replace: true });
        return;
      }

      // Allow many kinds of storeType; validate basic format instead of a fixed list
      if (!/^[a-z0-9_-]+$/i.test(storeType)) {
        navigate('/auth/store-selector?error=invalid-store-type', { replace: true });
        return;
      }

      let store = verifiedStores.find(
        (s) => s.storeId === storeId && s.storeType === storeType
      );

      // If not found in verifiedStores, try falling back to currentStoreInfo
      if (!store) {
        if (
          currentStoreInfo &&
          currentStoreInfo.storeId === storeId &&
          currentStoreInfo.storeType === storeType &&
          currentStoreInfo.authorized === true
        ) {
          store = currentStoreInfo;
        } else {
          navigate('/auth/store-selector?error=unauthorized', { replace: true });
          return;
        }
      }

      // Check onboarding status using the effective `store` object
      const needsKycApproval = !(
        store.authorized === true && isStoreKycApprovedForPublishing(store)
      );

      if (
        needsKycApproval &&
        location.pathname.includes('/webbuilder') &&
        !location.pathname.includes('/settings')
      ) {
        setPendingKycRedirectPath(`/${storeType}/${storeId}/settings?tab=business&kycRequired=webbuilder`);
        setShowKycRedirectModal(true);
        setValidating(false);
        return;
      }

      setHasAccess(true);
      setValidating(false);
    }

    checkAccess();
  }, [statusLoading, isAuthenticated, verifiedStores, currentStoreInfo, storeType, storeId, navigate, location]);

  if (statusLoading || validating)
    return (
      <WebBuilderPreloader
        message={statusLoading ? "Loading page..." : "Validating access..."}
      />
    );
  if (!hasAccess && !showKycRedirectModal) return null;

  return (
    <>
      {hasAccess ? children : null}

      <Dialog open={showKycRedirectModal} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>KYC Approval Required</DialogTitle>
            <DialogDescription>
              Web Builder is available only after your KYC is approved. You will now be redirected to
              Business Information so you can complete verification.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowKycRedirectModal(false);
                if (storeType && storeId) {
                  navigate(`/${storeType}/${storeId}/dashboard`, { replace: true });
                } else {
                  navigate('/auth/store-selector', { replace: true });
                }
              }}
            >
              Not now
            </Button>
            <Button
              onClick={() => {
                setShowKycRedirectModal(false);
                navigate(
                  pendingKycRedirectPath || `/${storeType}/${storeId}/settings?tab=business&kycRequired=webbuilder`,
                  { replace: true },
                );
              }}
            >
              Go to Business Info
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
